import {type Epic, ofType} from 'redux-observable';
import {of, merge, interval, defer} from 'rxjs';
import {auditTime, catchError, filter, map, mergeMap, skip, take} from 'rxjs/operators';

import {combineEpics} from 'web-app/util/redux-observable';
import * as NotificationActions from 'web-app/react/notifications/actions';
import * as NotificationCountsActions from 'web-app/react/notifications/notification-counts/actions';
import {documentResumeObservable} from 'web-app/react/lifecycle/epics';
import PlatformHelper from 'web-app/helpers/platform-helper';
import * as PushNotificationsService from 'web-app/services/push-notifications';
import {type InferAction} from 'web-app/util/redux/typed-action-helpers';
import {type RootState} from 'web-app/react/redux/root-state';

import * as NotificationCenterActions from '../notification-center/actions';
import * as Actions from './actions';
import * as Helpers from './helpers';
import * as Api from '../api';

const updateNotificationCount = action$ =>
    action$
        .ofType(
            NotificationCenterActions.markNotificationAsReadSuccess.type,
            // NotificationCenterActions.markAllNotificationsAsSeenSuccess.type,
        )
        .mergeMap(() => of(Actions.getNotificationCount.action({immediate: true})));

const getNotificationCount: Epic<InferAction<typeof Actions['getNotificationCount']>, any, RootState, {}> = action$ => {
    const getNotificationCount$ = action$.pipe(ofType(Actions.getNotificationCount.type));
    const immediate$ = merge(
        getNotificationCount$.pipe(take(1)),
        getNotificationCount$.pipe(filter(action => action.payload.immediate ?? false)),
    );
    // We want to minimize the number of requests sent to the notification count API. We
    // utilize multicast to trigger the first event in the app lifetime immediately while all
    // subsequent events are only triggered at-most-once every 10 seconds.
    const throttled$ = getNotificationCount$.pipe(
        skip(1),
        filter(action => action.payload.immediate !== true),
        auditTime(10000),
    );
    const actionObservable$ = merge(immediate$, throttled$);

    const graphQLUpdates$ = defer(() =>
        Api.getNotificationCountsFromGraphQL().pipe(
            map(response => Helpers.transformGraphQL(response.data)),
            map(response => Actions.getNotificationCountSuccess.action(response)),
        ),
    );

    return merge(actionObservable$.pipe(mergeMap(() => graphQLUpdates$))).pipe(
        catchError(e => of(Actions.getNotificationCountFailed.action(e))),
    );
};

const listenForUpdates = action$ =>
    action$.ofType(Actions.listenForUpdates.type).mergeMap(() => {
        const millisecondsBetweenUpdates = 2 * 60 * 1000; // Two minutes
        const intervalRefresh = interval(millisecondsBetweenUpdates);

        // If we are already browsing the app
        const didReceivePush = action$
            .ofType(NotificationActions.didReceiveNotification.type)
            .filter(
                ({payload}: ReturnType<typeof NotificationActions.didReceiveNotification.action>) => payload.foreground,
            );

        const refreshCountsAction = NotificationCountsActions.getNotificationCount.action();

        // Refresh counts on an interval, for foreground push, web socket response, and on document resume.
        // Refresh counts initially
        return merge<any>(intervalRefresh, didReceivePush, documentResumeObservable).pipe(
            map(() => refreshCountsAction),
        );
    });

const updateAppBadge = action$ =>
    action$
        .ofType(Actions.getNotificationCountSuccess.type)
        .do(({payload}) => {
            if (PlatformHelper.isMobileApp()) {
                const badgeCount = payload.response.conversations.OWN.unread + payload.response.notifications.unseen;
                PushNotificationsService.setBadgeCount(badgeCount);
            }
        })
        // Don't let any actions through, otherwise we'll end up in an infinte loop
        .filter(() => false);

export default combineEpics(updateNotificationCount, getNotificationCount, listenForUpdates, updateAppBadge);
